import type { ITestIdea } from '../types';

import { createContext, useContext, useMemo, useState } from 'react';
import { DEMO_TEST_IDEAS, TEST_IDEAS_SIDE_BY_SIDE_MEDIA_QUERY } from '../constants';
import { isBrowser } from '~/utils';

export type ITestIdeasContext = {
  isSidebarOpen: boolean;
  handleToggleSidebar: () => void;
  handleCloseSidebar: () => void;
  isIdeaDetailOpen: boolean;
  activeIdea?: ITestIdea;
  handleOpenIdeaDetail: (ideaId: string) => void;
  handleCloseIdeaDetail: () => void;
};

type IProps = {
  children: React.ReactNode;
};

const TestIdeasContext = createContext<ITestIdeasContext | undefined>(undefined);

/**
 * The sidebar starts open so a new merchant sees the ideas beside the report, except on frames
 * too narrow to fit it. Closing the drawer keeps the last idea so its copy stays as it slides out.
 */
export const TestIdeasProvider = ({ children }: IProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => isBrowser() && window.matchMedia(TEST_IDEAS_SIDE_BY_SIDE_MEDIA_QUERY).matches,
  );
  const [isIdeaDetailOpen, setIsIdeaDetailOpen] = useState(false);
  const [activeIdeaId, setActiveIdeaId] = useState<string>();

  const contextValue = useMemo<ITestIdeasContext>(
    () => ({
      isSidebarOpen,
      handleToggleSidebar: () => setIsSidebarOpen((isOpen) => !isOpen),
      handleCloseSidebar: () => setIsSidebarOpen(false),
      isIdeaDetailOpen,
      activeIdea: DEMO_TEST_IDEAS.find((idea) => idea.id === activeIdeaId),
      handleOpenIdeaDetail: (ideaId) => {
        setActiveIdeaId(ideaId);
        setIsIdeaDetailOpen(true);
      },
      handleCloseIdeaDetail: () => setIsIdeaDetailOpen(false),
    }),
    [isSidebarOpen, isIdeaDetailOpen, activeIdeaId],
  );

  return <TestIdeasContext.Provider value={contextValue}>{children}</TestIdeasContext.Provider>;
};

export type IUseTestIdeasContextResult = ITestIdeasContext;

export const useTestIdeasContext = (): IUseTestIdeasContextResult => {
  const context = useContext(TestIdeasContext);
  if (!context) {
    throw new Error('useTestIdeasContext must be used within a TestIdeasProvider');
  }
  return context;
};
